import { cloneDeep, differenceBy, intersectionBy } from 'lodash';
import { type Dispatch, type SetStateAction, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';

import { useRewardTierApplied } from '@fhs/loyalty/src/hooks/use-reward-tier-applied';
import { ICartEntryFragment, OfferType } from '@fhs-legacy/frontend/src/generated/rbi-graphql';
import usePosVendor from '@fhs-legacy/frontend/src/hooks/menu/use-pos-vendor';
import { useLocalStorageState } from '@fhs-legacy/frontend/src/hooks/use-local-storage-state';
import { useToast } from '@fhs-legacy/frontend/src/hooks/use-toast';
import { IAddToCartSelectionAttributes } from '@fhs-legacy/frontend/src/state/amplitude/types';
import { useCRMEventsContext } from '@fhs-legacy/frontend/src/state/crm-events';
import {
  actions,
  selectors,
  useAppDispatch,
  useAppSelector,
} from '@fhs-legacy/frontend/src/state/global-state';
import { removeAppliedOffersInStorage } from '@fhs-legacy/frontend/src/state/global-state/models/loyalty/offers/offers.utils';
import { removeAppliedRewardsInStorage } from '@fhs-legacy/frontend/src/state/global-state/models/loyalty/rewards/rewards.utils';
import { useIsLoyaltyEnabled } from '@fhs-legacy/frontend/src/state/loyalty/hooks/use-is-loyalty-enabled';
import { useLoyaltyUser } from '@fhs-legacy/frontend/src/state/loyalty/hooks/use-loyalty-user';
import usePricingFunction from '@fhs-legacy/frontend/src/state/menu-options/hooks/use-pricing-function';
import { useOffersContext } from '@fhs-legacy/frontend/src/state/offers';
import { TipAmounts } from '@fhs-legacy/frontend/src/state/order';
import { useCartTip } from '@fhs-legacy/frontend/src/state/order/hooks/use-cart-tip';
import {
  findEntriesByCmsId,
  replaceEntry,
  replaceEntryArrayItem,
} from '@fhs-legacy/frontend/src/state/order/utils';
import { createCartEntry } from '@fhs-legacy/frontend/src/utils/cart';
import { isTest } from '@fhs-legacy/frontend/src/utils/environment';
import LocalStorage, { StorageKeys } from '@fhs-legacy/frontend/src/utils/local-storage';
import { parseStringifiedJSON } from '@fhs-legacy/frontend/src/utils/parse-string';
import { TOAST_ITEM_ADDED_TO_CART } from '@fhs-legacy/frontend/src/utils/test-ids';
import { IBackendCartEntries, ICartEntry } from '@rbi-ctg/menu';

import type { ICartActions } from '../../cart-types';

import { useCartStore } from './cart-store-values';
import {
  removeOfferFromCart,
  removeRewardIfNeeded,
  shouldOfferBeRemoved,
  shouldOfferV2BeRemoved,
} from './loyalty-helpers';

/**
 * functions to manipulate the state in the cartStore
 * if you need the values from the store - useCartValues
 */
export const useCartActions: () => ICartActions = () => {
  const { vendor } = usePosVendor();
  const { pricingFunction } = usePricingFunction({ vendor });
  const {
    cartEntries,
    addItemToCartStore,
    removeItemFromCartStore,
    replaceItemsInCartStore,
    emptyCartStore,
  } = useCartStore();
  const toast = useToast();
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();

  const { logAddOrRemoveFromCart } = useCRMEventsContext();
  const offers = useOffersContext();

  const { setTipAmount, setTipSelection } = useCartTip();
  const loyaltyEnabled = useIsLoyaltyEnabled();
  const { loyaltyUser } = useLoyaltyUser();
  const { rewardTierApplied } = useRewardTierApplied();

  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const selectedOfferSelections = useAppSelector(selectors.loyalty.selectSelectedOfferSelections);
  const discountAppliedCmsOffers = useAppSelector(selectors.loyalty.selectDiscountAppliedCmsOffer);

  // Pick Up Tips
  const [, , clearSelectedPickupTip] = useLocalStorageState({
    key: StorageKeys.PICK_UP_TIPS_SELECTED,
    defaultReturnValue: '0',
  });

  // Pick Up Tips in case user selected "Other" option
  const [, , clearOtherPickupTip] = useLocalStorageState({
    key: StorageKeys.OTHER_PICK_UP_TIPS_SELECTED,
    defaultReturnValue: undefined,
  });

  /**
   * Adds a single cartEntry to the cart
   */
  const addItemToCart = useCallback(
    (
      cartEntry: ICartEntry | IBackendCartEntries | ICartEntryFragment | null,
      eventAttrs?: IAddToCartSelectionAttributes | undefined,
      showMessage = true
    ) => {
      const previousCartEntries = [...cartEntries];
      const typedCartEntry = cartEntry as ICartEntry;

      addItemToCartStore(typedCartEntry);

      logAddOrRemoveFromCart({
        action: 'add',
        cartEntry: typedCartEntry,
        previousCartEntries,
        selectionAttrs: eventAttrs,
      });

      if (cartEntry?.isDonation) {
        return;
      }

      if (showMessage) {
        toast.show({
          testID: TOAST_ITEM_ADDED_TO_CART,
          duration: isTest ? 9999999 : undefined,
          text: formatMessage({ id: 'addToCartSuccess' }, { itemName: cartEntry?.name }),
          variant: 'positive',
        });
      }
    },
    [cartEntries, addItemToCartStore, formatMessage, logAddOrRemoveFromCart, toast]
  );

  /**
   * Adds multiple cart entries to the cart
   */
  const addMultipleItemsToCart = useCallback(
    ({
      newCartEntries,
      eventAttrs,
      shouldShowMessage = true,
    }: {
      newCartEntries: ICartEntry[];
      eventAttrs?: IAddToCartSelectionAttributes;
      shouldShowMessage?: boolean;
    }) => {
      newCartEntries.forEach(entry => {
        addItemToCart(entry, eventAttrs, shouldShowMessage);
      });
    },
    [addItemToCart]
  );

  /**
   * Adds reorder to cart using the enrichCartEntriesResponse
   */
  const addReorderItemsToCart = useCallback(
    (newCartEntries: IBackendCartEntries[]) => {
      newCartEntries.forEach(entry => {
        const modifiedEntry = {
          ...entry,
          children: parseStringifiedJSON({ value: (entry.children as unknown as string) || '[]' }),
          // If image is already an object, use it directly; only parse if it's a string
          image:
            typeof entry.image === 'object' && entry.image !== null
              ? entry.image
              : parseStringifiedJSON({ value: entry.image || '{}' }),
          pickerSelections: parseStringifiedJSON({ value: entry.pickerSelections || '{}' }),
        };
        addItemToCart(modifiedEntry, undefined, false);
      });
    },
    [addItemToCart]
  );

  /**
   *  Removes a single cartEntry using its cartId, expects object
   */
  const removeItemFromCart = useCallback(
    ({ cartId, skipLoyaltyActions }: { cartId: string; skipLoyaltyActions?: boolean }) => {
      const isOffer = !!appliedOffers.find(offer => offer.cartId === cartId);
      const previousCartEntries = [...cartEntries];
      const cartEntryToRemove = previousCartEntries.find(
        (entry: ICartEntry) => entry.cartId === cartId
      );

      if (!cartEntryToRemove) {
        return;
      }

      logAddOrRemoveFromCart({
        action: 'remove',
        cartEntry: cartEntryToRemove,
        previousCartEntries,
      });

      removeItemFromCartStore(cartEntryToRemove);

      // Skip loyalty actions during offer replacement to prevent duplicate cleanup.
      //
      // When replacing offers: old offer removal → new offer addition → quantity adjustments.
      // The removeItemFromCart() call would trigger loyalty actions again on stale cart state,
      // causing unnecessary resetAppliedOffers() calls and potential state corruption.
      if (skipLoyaltyActions) {
        return;
      }

      if (shouldOfferV2BeRemoved(cartEntryToRemove, selectedOfferSelections)) {
        dispatch(actions.loyalty.resetAppliedOffers());
      }

      if (
        isOffer ||
        shouldOfferBeRemoved(
          previousCartEntries,
          cartEntryToRemove,
          offers,
          loyaltyEnabled,
          rewardTierApplied
            ? {
                id: rewardTierApplied.id,
                name: rewardTierApplied.name,
                points: rewardTierApplied.points,
              }
            : undefined,
          selectedOfferSelections,
          discountAppliedCmsOffers
        )
      ) {
        dispatch(actions.loyalty.removeAppliedOfferByCartEntry(cartEntryToRemove));
        removeOfferFromCart(offers);
      }
      // removes applied rewards associated to cart entry on removal
      removeRewardIfNeeded(
        cartEntryToRemove,
        dispatch,
        rewardTierApplied
          ? {
              id: rewardTierApplied.id,
              name: rewardTierApplied.name,
              points: rewardTierApplied.points,
            }
          : undefined,
        selectedOfferSelections,
        loyaltyEnabled
      );
      // Resets the availability of Surprise so it wont show in cart page
      dispatch(actions.loyalty.resetSurpriseAvailability());
    },
    [
      cartEntries,
      removeItemFromCartStore,
      appliedOffers,
      rewardTierApplied,
      discountAppliedCmsOffers,
      dispatch,
      logAddOrRemoveFromCart,
      loyaltyEnabled,
      offers,
      selectedOfferSelections,
    ]
  );

  /**
   * Removes all provided cartEntries from the cart using their cartIds
   */
  const removeAllItemsFromCart = useCallback(
    (cartEntriesToRemove: ICartEntry[]) => {
      return cartEntriesToRemove.forEach(entry => removeItemFromCart(entry));
    },
    [removeItemFromCart]
  );

  /**
   * Updates a singe cart entry
   */
  const updateCartEntry = useCallback(
    (newCartEntry: ICartEntry, originalCartEntry: ICartEntry) => {
      // update item in Amplitude cart
      logAddOrRemoveFromCart({
        action: 'remove',
        cartEntry: originalCartEntry,
        previousCartEntries: [originalCartEntry],
      });
      logAddOrRemoveFromCart({
        action: 'add',
        cartEntry: newCartEntry,
        previousCartEntries: [originalCartEntry],
      });

      const updatedCartEntries = replaceEntryArrayItem(cartEntries, newCartEntry);

      replaceItemsInCartStore(updatedCartEntries);

      toast.show({
        text: formatMessage({ id: 'updateCartSuccess' }, { itemName: newCartEntry.name }),
        variant: 'positive',
      });
    },
    [cartEntries, replaceItemsInCartStore, formatMessage, logAddOrRemoveFromCart, toast]
  );

  /**
   * Updates multiple CartEntries at once
   */
  const updateMultipleCartEntries = useCallback(
    (entriesToUpdate: ICartEntry[], allOriginalCartEntries?: ICartEntry[]) => {
      if (!allOriginalCartEntries) {
        return;
      }

      entriesToUpdate.forEach(newEntry => {
        const foundEntry = allOriginalCartEntries?.find(
          originalEntry => originalEntry.cartId === newEntry.cartId
        );

        if (!foundEntry) {
          return;
        }
        return updateCartEntry(newEntry, foundEntry);
      });
    },
    [updateCartEntry]
  );

  /**
      Adds or Updates CartEntries
      Accepts CartEntries and will determine which ones are being updated and which ones are new using their cartIds
      If there are no items in the cart already just add all new entries to the cart
     */
  const upsertCart = useCallback(
    (newCartEntries: ICartEntry[], eventAttrs?: IAddToCartSelectionAttributes) => {
      if (!cartEntries.length) {
        addMultipleItemsToCart({ newCartEntries, eventAttrs });
        return;
      }

      const existingEntries = intersectionBy(newCartEntries, cartEntries, 'cartId');
      const newEntries = differenceBy(newCartEntries, cartEntries, 'cartId');

      addMultipleItemsToCart({ newCartEntries: newEntries, eventAttrs });
      updateMultipleCartEntries(existingEntries, cartEntries);
    },
    [cartEntries, addMultipleItemsToCart, updateMultipleCartEntries]
  );

  /**
   * Updates the number of a particular item in a cart
   */
  const updateCartItemsQuantity = useCallback(
    (cartId: string, quantity: number) => {
      if (quantity < 1) {
        return removeItemFromCart({ cartId });
      }

      const updatedCartEntries = cartEntries.map(entry => {
        const entryCopy = { ...entry };
        if (entryCopy.cartId === cartId) {
          entryCopy.quantity = quantity;
          const nextPrice = pricingFunction(entry, entryCopy.quantity);
          if (nextPrice !== 0 || entryCopy.price == null) {
            entryCopy.price = nextPrice;
          } else {
            const previousQuantity = entry.quantity || 1;
            const unitPrice = entryCopy.price / previousQuantity;
            entryCopy.price = Math.round(unitPrice * quantity);
          }
        }

        return entryCopy;
      });

      replaceItemsInCartStore(updatedCartEntries);
    },
    [cartEntries, replaceItemsInCartStore, removeItemFromCart, pricingFunction]
  );

  /**
   * Used in order context, empties cart along with local storage, offers, rewards, loyalty, tips, zero donation amount and cart id is editing
   */
  const emptyCart = useCallback(
    (
      setCartIdEditing: Dispatch<SetStateAction<string>>,
      isDonationZeroAmountSelected: boolean,
      setIsDonationZeroAmountSelected: Dispatch<SetStateAction<boolean>>
    ) => {
      emptyCartStore();

      setCartIdEditing('');
      offers.clearSelectedOffer({ doLog: false });
      offers.setRedemptionMethod(null);
      setTipAmount(0);
      setTipSelection({
        percentAmount: TipAmounts.PERCENT_DEFAULT,
        dollarAmount: TipAmounts.DOLLAR_DEFAULT,
        otherAmount: 0,
        isOtherSelected: false,
      });

      clearSelectedPickupTip();
      clearOtherPickupTip();

      removeAppliedOffersInStorage();
      removeAppliedRewardsInStorage();

      if (loyaltyEnabled) {
        dispatch(actions.loyalty.resetAppliedOffers());
        dispatch(
          actions.loyalty.resetLoyaltyRewardsState({
            points: loyaltyUser?.points ?? 0,
            shouldResetAvailableRewardsMap: false,
          })
        );
      }
      if (isDonationZeroAmountSelected) {
        setIsDonationZeroAmountSelected(false);
      }

      LocalStorage.removeItem(StorageKeys.ORDER_COMBO_SELECTED_PICKERS);
    },
    [
      emptyCartStore,
      clearOtherPickupTip,
      clearSelectedPickupTip,
      dispatch,
      loyaltyEnabled,
      loyaltyUser?.points,
      offers,
      setTipAmount,
      setTipSelection,
    ]
  );

  /**
   * Is cart currently swapping cart entries with offer entries
   */
  const isCartEntryInSwaps: (cartEntry: ICartEntry) => boolean = useCallback(
    cartEntry => {
      return appliedOffers.some(offer =>
        cartEntry?.children.length
          ? offer.cartId === cartEntry.cartId
          : offer?.swap?.cartId === cartEntry.cartId
      );
    },
    [appliedOffers]
  );

  /**
   * Swaps normal cart entries with offer entries
   * TODO: needs type fix for (to: IItem, swapOffer: ISwappableItem)
   */
  const swapCartItems = useCallback(
    (to: any, swapOffer: any) => {
      const prevEntries = cartEntries;
      const { from, ...offer } = swapOffer;

      // Root cart entry is needed for appliedOffers
      // Child cart entry is needed for swaps objects
      const { rootEntry } = findEntriesByCmsId(prevEntries, from);
      let entriesToReturn = prevEntries;

      if (rootEntry) {
        const oldEntry = rootEntry;
        // Define price for item
        const oldEntryUnitaryPrice = (oldEntry?.price ?? 0) / oldEntry.quantity;
        const { type: swapType, offerId, offerType, cmsId } = offer;

        const entryToSwap = {
          ...createCartEntry({
            item: to,
            price: oldEntry.children.length ? 0 : oldEntryUnitaryPrice,
            quantity: 1,
          }),
          sanityId: to._id,
        };

        const offerToApply = {
          id: offerId,
          cartId: oldEntry.children.length ? oldEntry.cartId : entryToSwap.cartId,
          type: OfferType[offerType],
          swap: {
            cartId: entryToSwap.cartId,
            from,
            to: to._id,
            swapType,
          },
          cmsId,
        };

        // This covers the case when a cartEntry has more than one item and swap is selected
        // updating price and quantity to the cartEntry that was swapped
        if (oldEntry.quantity > 1) {
          oldEntry.quantity--;
          oldEntry.price = oldEntryUnitaryPrice * oldEntry.quantity;

          // If cartEntry has children, we keep a copy of the entry without
          // the swap applied to concat to the entries array after applying the swap
          if (oldEntry.children.length) {
            const splittedItem = cloneDeep(oldEntry);
            splittedItem.cartId = uuidv4();
            offerToApply.cartId = oldEntry.cartId;
            // Creating new cart entry with swap applied to replace at root level
            // having quantity and price correct for a swapped item
            const newRoot = {
              ...oldEntry,
              children: replaceEntry(oldEntry.children, from, { ...entryToSwap, price: 0 }),
              price: oldEntryUnitaryPrice,
              quantity: 1,
            };

            entriesToReturn = replaceEntry(prevEntries, oldEntry._id, newRoot).concat(splittedItem);
          } else {
            entriesToReturn = prevEntries.concat(entryToSwap);
          }
        } else {
          entriesToReturn = replaceEntry(prevEntries, from, entryToSwap);
        }

        dispatch(actions.loyalty.applyOffer(offerToApply));
      }

      replaceItemsInCartStore(entriesToReturn);
      return entriesToReturn;
    },
    [cartEntries, replaceItemsInCartStore, dispatch]
  );

  return {
    addItemToCart,
    addMultipleItemsToCart,
    addReorderItemsToCart,
    removeItemFromCart,
    removeAllItemsFromCart,
    updateCartEntry,
    updateMultipleCartEntries,
    upsertCart,
    updateCartItemsQuantity,
    emptyCart,
    isCartEntryInSwaps,
    swapCartItems,
  };
};
